import {
  jointPose,
  MOTIONS,
  motionDuration,
  strideLength,
  type Facing,
  type Motion,
  type Joint,
} from "./motion";
import type { Animated, Vec2 } from "./types";
import { drawPaintedCore } from "./painted-cores";
import { drawInkActor, drawInkObject } from "./ink-art";
export interface ArtState {
  motion: Motion;
  phase: number;
  facing?: Facing;
  time?: number;
  health?: number;
  active?: boolean;
  audit?: boolean;
  travel?: Vec2;
  gait?: { motion: "walk" | "run"; phase: number };
}
const ink = "#30443b",
  tau = Math.PI * 2;
const colors: Record<string, [string, string]> = {
  hero: ["#538c78", "#b7c894"],
  pumpkin: ["#d68d45", "#f2bd6c"],
  coconut: ["#9d8061", "#e2d2a1"],
  wintermelon: ["#75a598", "#c6dfb0"],
  banana: ["#77a062", "#ced594"],
  moonherb: ["#8d9fc0", "#e1d5e9"],
  carrot: ["#d29458", "#f3ca7e"],
  chili: ["#b95c4d", "#f3a872"],
  mite: ["#8c6980", "#d19e9b"],
  brute: ["#807f74", "#b6b09a"],
  spitter: ["#a58baa", "#dcc0c4"],
  sentinel: ["#968257", "#d9bc7c"],
  moth: ["#9c9bbb", "#d6d9dc"],
  "thorn-warden": ["#6b8a65", "#b8c599"],
  "ember-tortoise": ["#986957", "#deaa69"],
};
export function ellipse(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  color: string,
  stroke = true,
) {
  ctx.fillStyle = color;
  if (
    stroke &&
    rx > 10 &&
    ry > 12 &&
    typeof color === "string" &&
    /^#[0-9a-f]{6}$/i.test(color)
  ) {
    const light = ctx.createRadialGradient(
      x - rx * 0.38,
      y - ry * 0.45,
      1,
      x,
      y,
      Math.max(rx, ry) * 1.15,
    );
    light.addColorStop(0, color);
    light.addColorStop(0.56, color);
    light.addColorStop(1, "#415b49");
    ctx.fillStyle = light;
  }
  ctx.beginPath();
  ctx.ellipse(x, y, Math.max(0.1, rx), Math.max(0.1, ry), 0, 0, tau);
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = ink;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}
export function shape(
  ctx: CanvasRenderingContext2D,
  points: Joint[],
  color: string,
  stroke = true,
) {
  ctx.fillStyle = color;
  ctx.beginPath();
  points.forEach((p, i) => (i ? ctx.lineTo(...p) : ctx.moveTo(...p)));
  ctx.closePath();
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = ink;
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}
function line(
  ctx: CanvasRenderingContext2D,
  points: Joint[],
  color: string,
  width = 2,
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  points.forEach((p, i) => (i ? ctx.lineTo(...p) : ctx.moveTo(...p)));
  ctx.stroke();
}
function leaf(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  angle: number,
  color: string,
) {
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(angle);
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(-size, -size, size * 0.15, -size * 1.8);
  ctx.quadraticCurveTo(size, -size, 0, 0);
  ctx.fill();
  line(
    ctx,
    [
      [0, 0],
      [size * 0.1, -size * 1.45],
    ],
    "#3d604b88",
    1,
  );
  ctx.restore();
}
function eye(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  closed = false,
) {
  if (closed)
    line(
      ctx,
      [
        [x - 2, y],
        [x + 2, y],
      ],
      ink,
      2,
    );
  else {
    ellipse(ctx, x, y, 2.2, 3.2, ink, false);
    ellipse(ctx, x + 0.6, y - 1, 0.6, 0.8, "#f9efd3", false);
  }
}
function canopy(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  color: string,
  seed: number,
) {
  const g = ctx.createRadialGradient(x - rx * 0.3, y - ry * 0.4, 1, x, y, rx);
  g.addColorStop(0, color);
  g.addColorStop(1, "#547451");
  shape(
    ctx,
    Array.from({ length: 28 }, (_, i) => {
      const a = (i / 28) * tau,
        k = 1 + Math.sin(i * 3.7 + seed) * 0.09;
      return [x + Math.cos(a) * rx * k, y + Math.sin(a) * ry * k] as Joint;
    }),
    g as unknown as string,
    false,
  );
  for (let k = 0; k < 18; k++) {
    const a = k * 2.4,
      d = Math.sqrt((k + 1) / 18),
      px = x + Math.cos(a) * rx * d * 0.9,
      py = y + Math.sin(a) * ry * d * 0.85;
    leaf(
      ctx,
      px,
      py,
      4 + (k % 3),
      -0.4 + (k % 5) * 0.3,
      k % 3 ? color : "#b1be78",
    );
  }
}
function branch(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  time: number,
  index: number,
  size = 1,
) {
  const bend = Math.sin(time * 1.1 + index * 2.3) * 3;
  line(
    ctx,
    [
      [x, y],
      [x + bend * 0.35, y - 14 * size],
      [x + bend, y - 27 * size],
    ],
    "#658661",
    1.8,
  );
  for (let k = 0; k < 4; k++)
    leaf(
      ctx,
      x + (bend * k) / 5,
      y - k * 6 * size,
      5 * size,
      (k % 2 ? 1 : -1) * 0.9,
      "#88a369",
    );
}
export function drawObject(
  ctx: CanvasRenderingContext2D,
  id: string,
  s: ArtState,
) {
  if (drawInkObject(ctx, id, s)) return;
  const t = s.time ?? s.phase * 2,
    depleted = s.active === false || s.motion === "depleted",
    hurt = s.motion === "hurt";
  if (s.motion === "harvest") {
    ctx.save();
    ctx.globalAlpha = Math.max(0.06, 1 - s.phase);
    for (let i = 0; i < 9; i++)
      leaf(
        ctx,
        Math.sin(i * 2.4) * 28,
        -50 + s.phase * 45 + Math.cos(i) * 18,
        5,
        i + s.phase,
        "#b7bd79",
      );
    ctx.restore();
    return;
  }
  if (id.startsWith("decor-")) {
    const kind = id.slice(6);
    if (kind === "fern" || kind === "flowers" || kind === "reed") {
      for (let i = 0; i < 5; i++) {
        branch(ctx, i * 7 - 14, 0, t, i, kind === "reed" ? 1.3 : 0.65);
        if (kind === "flowers")
          for (let k = 0; k < 5; k++)
            ellipse(
              ctx,
              i * 7 - 14 + Math.cos(k * 1.256) * 3,
              -18 + Math.sin(k * 1.256) * 3,
              2,
              2,
              "#dedcb6",
              false,
            );
      }
    } else if (kind === "log" || kind === "root") {
      line(
        ctx,
        [
          [-26, 0],
          [-13, -9],
          [10, -5],
          [24, -15],
        ],
        "#6a7756",
        kind === "log" ? 13 : 5,
      );
      line(
        ctx,
        [
          [-20, -3],
          [-9, -8],
          [13, -6],
        ],
        "#a4a979",
        2,
      );
      branch(ctx, 0, -6, t, 2, 0.5);
    } else if (kind === "ruin") {
      shape(
        ctx,
        [
          [-14, 0],
          [-17, -27],
          [-8, -39],
          [11, -34],
          [16, -3],
        ],
        "#8c9d84",
      );
      line(
        ctx,
        [
          [-5, -29],
          [5, -25],
          [-4, -17],
          [4, -11],
        ],
        "#c4cbae",
        2,
      );
      branch(ctx, 10, 0, t, 2, 0.5);
    } else if (kind === "stones") {
      for (let i = 0; i < 3; i++)
        shape(
          ctx,
          [
            [i * 14 - 20, 0],
            [i * 14 - 18, -8],
            [i * 14 - 10, -11],
            [i * 14 - 6, -2],
          ],
          "#a6ae91",
        );
    } else if (kind === "shroom") {
      for (let i = 0; i < 3; i++) {
        line(
          ctx,
          [
            [i * 13 - 13, 0],
            [i * 13 - 13, -14 - (i % 2) * 8],
          ],
          "#bdc6a8",
          3,
        );
        ellipse(ctx, i * 13 - 13, -14 - (i % 2) * 8, 8, 5, "#b7bea1");
      }
    }
    return;
  }
  ellipse(ctx, 4, 2, 29, 8, "#203d3228", false);
  if (s.motion === "cast" || s.motion === "interact") {
    for (let i = 0; i < 8; i++) {
      const a = (i * tau) / 8 + s.phase * 2;
      ellipse(
        ctx,
        Math.cos(a) * 30,
        -25 - Math.sin(s.phase * Math.PI) * 42 + Math.sin(a) * 9,
        2,
        3,
        "#efdf9b",
        false,
      );
    }
  }
  if (hurt) {
    for (let i = 0; i < 6; i++) {
      const a = i * 2.4;
      leaf(
        ctx,
        Math.cos(a) * s.phase * 40,
        -30 - Math.sin(a) * s.phase * 25,
        3,
        a,
        id === "tree" ? "#c3ce91" : "#d7d5ad",
      );
    }
  }
  if (id === "tree") {
    if (depleted) {
      shape(
        ctx,
        [
          [-17, 0],
          [-13, -21],
          [12, -19],
          [18, 0],
        ],
        "#99815b",
      );
      ellipse(ctx, 0, -20, 13, 4, "#d2b886");
      return;
    }
    shape(
      ctx,
      [
        [-15, 1],
        [-6, -25],
        [-8, -78],
        [3, -100],
        [12, -49],
        [9, -12],
        [24, 2],
      ],
      "#816f4f",
    );
    line(
      ctx,
      [
        [-3, -4],
        [1, -40],
        [-2, -68],
      ],
      "#cfb67a",
      3,
    );
    for (let i = 0; i < 10; i++) {
      let x = Math.cos(i * 2.4) * 26,
        y = -82 + Math.sin(i * 2.4) * 20;
      line(
        ctx,
        [
          [0, -37],
          [x, -60],
          [x, y],
        ],
        "#7c704f",
        4,
      );
      const w = Math.sin(t * 0.8 + i * 1.7) * 2;
      canopy(
        ctx,
        x + w,
        y,
        25 + (i % 3) * 3,
        21,
        ["#66895a", "#7f9b60", "#99ad6d"][i % 3],
        i,
      );
    }
    for (let i = 0; i < 3; i++) branch(ctx, -22 + i * 21, 0, t, i, 0.6);
  } else if (id === "rock" || id === "amber") {
    shape(
      ctx,
      [
        [-35, 0],
        [-29, -25],
        [-10, -41],
        [20, -34],
        [36, -9],
        [25, 3],
      ],
      "#758477",
    );
    shape(
      ctx,
      [
        [-29, -25],
        [-10, -41],
        [3, -17],
        [-8, -3],
      ],
      "#a1ab93",
    );
    if (!depleted)
      for (let i = 0; i < 3; i++) {
        const x = -16 + i * 15,
          h = 25 + (i % 2) * 17,
          c = id === "amber" ? "#d1a756" : "#76b8b5";
        shape(
          ctx,
          [
            [x - 7, -9],
            [x - 6, -h],
            [x, -h - 9],
            [x + 7, -h + 4],
            [x + 6, -10],
          ],
          c,
        );
        line(
          ctx,
          [
            [x, -h - 9],
            [x, -12],
          ],
          "#f5ecc798",
          1.3,
        );
        if (Math.sin(t * 1.5 + i * 3) > 0.87)
          line(
            ctx,
            [
              [x - 3, -h],
              [x + 4, -h + 8],
            ],
            "#fff6d5",
            2,
          );
      }
    if (hurt || (s.health ?? 1) < 0.7 || depleted)
      line(
        ctx,
        [
          [-12, -30],
          [-4, -20],
          [-8, -12],
          [8, -6],
        ],
        "#364c42",
        2.5,
      );
  } else if (id === "well") {
    for (let j = 0; j < 3; j++)
      for (let i = 0; i < 6; i++) {
        ctx.fillStyle = j % 2 ? "#9ba88d" : "#b7bb98";
        ctx.strokeStyle = ink;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(-29 + i * 10 + (j % 2) * 3, -34 + j * 10, 11, 11, 3);
        ctx.fill();
        ctx.stroke();
      }
    ellipse(ctx, 0, -35, 31, 13, "#c6c9a4");
    ellipse(ctx, 0, -35, 23, 8, "#488c8c");
    ellipse(ctx, 0, -35, 14 + Math.sin(t * 2) * 4, 4, "#a2d5c759");
    branch(ctx, 28, 0, t, 3, 0.9);
  } else if (id === "gate") {
    for (const side of [-1, 1]) {
      shape(
        ctx,
        [
          [side * 22, 0],
          [side * 33, -76],
          [side * 23, -101],
          [side * 13, -84],
          [side * 14, -5],
        ],
        "#9ba98d",
      );
      branch(ctx, side * 23, -42, t, side, 1.3);
    }
    shape(
      ctx,
      [
        [-33, -76],
        [-23, -101],
        [0, -111],
        [23, -101],
        [33, -76],
        [0, -91],
      ],
      "#b4be9b",
    );
    const g = ctx.createRadialGradient(0, -49, 1, 0, -49, 42);
    g.addColorStop(0, "#badccdaa");
    g.addColorStop(1, "#7497ae08");
    ellipse(ctx, 0, -48, 25, 43, g as unknown as string, false);
    for (let i = 0; i < 7; i++)
      ellipse(
        ctx,
        Math.sin(i * 4 + t * 0.3) * 15,
        -28 - i * 9,
        1.5,
        2,
        "#ecddb5",
        false,
      );
  } else if (id === "camp") {
    shape(
      ctx,
      [
        [-42, 0],
        [0, -60],
        [43, 0],
      ],
      "#b5a778",
    );
    shape(
      ctx,
      [
        [0, -60],
        [8, -5],
        [43, 0],
      ],
      "#718c6c",
    );
    shape(
      ctx,
      [
        [-14, 0],
        [0, -30],
        [13, 0],
      ],
      "#3f5a4b",
    );
    line(
      ctx,
      [
        [0, -63],
        [0, -76],
      ],
      "#7f775a",
      3,
    );
    leaf(ctx, 0, -70, 10, 1.3, "#abc093");
  } else {
    const crop = id.replace("crop-", "");
    const grow = s.motion === "grow" ? s.phase : 1;
    for (let i = 0; i < (depleted ? 2 : 5); i++)
      branch(ctx, -16 + i * 8, 0, t, i, grow * 0.7 + 0.3);
    if (depleted) return;
    if (id === "berry") {
      for (let i = 0; i < 9; i++)
        ellipse(
          ctx,
          Math.sin(i * 2.1) * 21,
          -13 - (i % 3) * 8,
          4,
          4,
          "#b55c62",
        );
    } else if (id === "herb") {
      for (let i = 0; i < 3; i++) {
        leaf(ctx, i * 13 - 14, -13, 9, 0, "#9fbbb1");
        ellipse(ctx, i * 13 - 14, -29, 2, 3, "#d4e9db");
      }
    } else if (grow > 0.35) {
      const c = colors[crop] ?? colors.pumpkin;
      if (crop === "banana" || crop === "coconut") {
        line(
          ctx,
          [
            [0, 0],
            [0, -40 * grow],
          ],
          "#8b8457",
          7,
        );
        for (let i = 0; i < 5; i++)
          leaf(
            ctx,
            0,
            -35 * grow,
            (18 + (i % 2) * 5) * grow,
            (i - 2) * 0.65,
            "#7b9e68",
          );
        for (let i = -1; i <= 1; i++)
          ellipse(
            ctx,
            i * 10,
            -24 * grow,
            crop === "coconut" ? 9 : 5,
            crop === "coconut" ? 9 : 13,
            c[0],
          );
      } else if (crop === "moonherb") {
        for (let i = 0; i < 6; i++)
          ellipse(
            ctx,
            Math.cos((i * tau) / 6) * 15,
            -27 + Math.sin((i * tau) / 6) * 12,
            10,
            7,
            c[1],
          );
        ellipse(ctx, 0, -27, 7, 7, "#e1cf82");
      } else if (crop === "chili") {
        for (let i = -1; i <= 1; i++)
          shape(
            ctx,
            [
              [i * 15 - 7, -22],
              [i * 15 + 5, -23],
              [i * 15 + 4, -8],
              [i * 15 - 6, -3],
              [i * 15 - 1, -11],
            ],
            c[0],
          );
      } else if (crop === "carrot") {
        for (let i = -1; i <= 1; i++) {
          shape(
            ctx,
            [
              [i * 17 - 7, -15],
              [i * 17 + 7, -16],
              [i * 17 + 1, 0],
            ],
            c[0],
          );
          leaf(ctx, i * 17, -15, 15, i * 0.4, "#77a164");
        }
      } else {
        ellipse(
          ctx,
          0,
          -19,
          (crop === "wintermelon" ? 29 : 26) * grow,
          21 * grow,
          c[0],
        );
        for (let k = -1; k <= 1; k++)
          line(
            ctx,
            [
              [k * 10, -36 * grow],
              [k * 14, -18 * grow],
              [k * 9, -2],
            ],
            c[1],
            2,
          );
        leaf(ctx, 0, -37 * grow, 11, 0.8, "#5e8555");
      }
    }
  }
}
export function drawLiving(
  ctx: CanvasRenderingContext2D,
  id: string,
  s: ArtState,
) {
  if (drawInkActor(ctx, id, s)) return;
  const c = colors[id];
  if (!c) {
    drawObject(ctx, id, s);
    return;
  }
  const p = jointPose(s.motion, s.phase),
    t = s.time ?? s.phase * 2,
    face = s.facing ?? "east",
    back = face === "north",
    closed =
      s.motion === "rest" ||
      s.motion === "death" ||
      (s.motion === "idle" && s.phase > 0.89 && s.phase < 0.94);
  ctx.save();
  if (face === "west") ctx.scale(-1, 1);
  if (s.motion === "death")
    ctx.globalAlpha = Math.max(0.15, 1 - s.phase * 0.85);
  const leg = (near: boolean) => {
    const k = near ? p.nearKnee : p.farKnee,
      f = near ? p.nearFoot : p.farFoot;
    const heavy = id === "thorn-warden" || id === "brute";
    line(ctx, [p.hip, k, f], near ? "#49614d" : "#364c46", heavy ? 13 : 7);
    ellipse(
      ctx,
      f[0] + 2,
      f[1] - 3,
      heavy ? 10 : 7,
      heavy ? 6 : 4,
      near ? "#8a795b" : "#586452",
    );
  };
  const arm = (near: boolean) => {
    const e = near ? p.nearElbow : p.farElbow,
      h = near ? p.nearHand : p.farHand,
      root = near ? p.nearShoulder : p.farShoulder;
    const heavy = id === "thorn-warden" || id === "brute";
    line(ctx, [root, e, h], ink, heavy ? 18 : 9);
    line(ctx, [root, e, h], near ? c[0] : "#526655", heavy ? 15 : 6);
    ellipse(
      ctx,
      ...h,
      heavy ? 10 : 4,
      heavy ? 11 : 4,
      id === "hero" ? "#ddc097" : c[1],
    );
    if (heavy) {
      line(
        ctx,
        [
          [e[0] - 3, e[1] - 6],
          [e[0] + 3, e[1] + 4],
          [h[0] - 2, h[1] + 4],
        ],
        c[1],
        1.4,
      );
      leaf(ctx, e[0] + 6, e[1] - 4, 8, 0.8, "#a2b17c");
    }
  };
  const pulse = Math.sin(s.phase * Math.PI),
    m = s.motion;
  const locomotion = ["walk", "run", "start", "stop"].includes(m),
    travel = m === "start" ? s.phase : m === "stop" ? 1 - s.phase : 1;
  const collapse = m === "death" ? s.phase : m === "rest" ? 0.45 : 0;
  const recoil = m === "hurt" ? -pulse * 6 : m === "stagger" ? -pulse * 10 : 0;
  if (id === "mite" || id === "ember-tortoise") {
    const count = id === "mite" ? 6 : 4;
    for (let i = 0; i < count; i++) {
      const side = i < count / 2 ? -1 : 1,
        phase =
          (s.phase + (i % (count / 2)) / (count / 2) + (side < 0 ? 0.5 : 0)) %
          1,
        w =
          locomotion || m === "rush"
            ? (phase < 0.5 ? 1 - 4 * phase : -1 + 4 * (phase - 0.5)) *
              (m === "run" ? 21 : 15) *
              travel
            : Math.sin(t + i) * 1.2,
        lift =
          m === "slam" && i < 2
            ? -Math.sin(Math.min(1, s.phase / 0.65) * Math.PI) * 24
            : locomotion
              ? phase < 0.5
                ? 0
                : -Math.sin((phase - 0.5) * tau) * 9 * travel
              : 0;
      line(
        ctx,
        [
          [side * 15, -33],
          [side * (25 + (i % 3) * 5) + w, -25 + collapse * 18],
          [side * (36 + (i % 3) * 5) + w - collapse * side * 12, lift],
        ],
        "#514f49",
        id === "mite" ? 4 : 8,
      );
    }
    const paintedShell = drawPaintedCore(ctx, id, 0, -7, 61, back);
    if (!paintedShell) {
      ellipse(ctx, 0, -34, 36, 27, c[0]);
      for (let i = 0; i < 5; i++)
        shape(
          ctx,
          [
            [i * 12 - 25, -43],
            [i * 12 - 29, -63],
            [i * 12 - 15, -46],
          ],
          c[1],
        );
    }
    const reach =
        m === "attack"
          ? pulse * 16
          : m === "rush"
            ? pulse * 8
            : m === "volley"
              ? -pulse * 8
              : m === "observe"
                ? Math.sin(s.phase * tau) * 5
                : m === "turn"
                  ? Math.sin(s.phase * tau) * 12
                  : 0,
      hx = back || face === "south" ? 0 : 28 + reach + recoil - collapse * 12,
      hy = back ? -64 : face === "south" ? -10 : -29 + collapse * 9;
    ellipse(ctx, hx, hy, 15, 13, c[1]);
    if (!back) {
      eye(ctx, hx + 5, hy - 3, closed);
      eye(ctx, hx - 5, hy - 3, closed);
    }
    if (m === "volley")
      ellipse(ctx, hx + 10, hy + 4, 3 + pulse * 6, 2 + pulse * 5, "#5f4239");
    if (m === "attack")
      for (const side of [-1, 1])
        line(
          ctx,
          [
            [hx + side * 6, hy + 8],
            [hx + side * (9 + pulse * 5), hy + 19],
            [hx + side * 2, hy + 16],
          ],
          ink,
          2,
        );
    if (id === "ember-tortoise" && !paintedShell)
      for (let i = 0; i < 7; i++) {
        const a = i * 2.4,
          x = Math.cos(a) * 22,
          y = -35 + Math.sin(a) * 17;
        shape(
          ctx,
          [
            [x - 9, y],
            [x - 5, y - 10],
            [x + 6, y - 11],
            [x + 12, y],
            [x + 5, y + 8],
            [x - 5, y + 7],
          ],
          i % 2 ? "#ae7751" : "#bd8c59",
        );
        ellipse(ctx, x, y, 3, 3, "#e7ba70", false);
      }
  } else if (id === "moth") {
    const beat = m === "run" ? 13 : locomotion ? 10 : 7,
      fold =
        collapse * 1.1 +
        (m === "heal"
          ? -pulse * 0.5
          : m === "attack"
            ? pulse * 0.7
            : m === "turn"
              ? Math.sin(s.phase * tau) * 0.25
              : recoil * 0.08);
    for (const side of [-1, 1]) {
      ctx.save();
      ctx.translate(side * 6, -50);
      ctx.rotate(
        side *
          (0.3 + Math.sin(t * beat) * 0.3 * (locomotion ? travel : 1) + fold),
      );
      ellipse(ctx, side * 23, -13, 25, 29, c[0]);
      ellipse(ctx, side * 20, 18, 20, 16, c[1]);
      ellipse(ctx, side * 27, -17, 9, 11, "#a9b7b0");
      ellipse(ctx, side * 27, -17, 4, 5, "#edddab");
      ctx.restore();
    }
    const paintedMoth = drawPaintedCore(ctx, id, 0, -11, 73, back);
    if (!paintedMoth) ellipse(ctx, 0, -44, 9, 31, "#746b78");
    if (!back && !paintedMoth) {
      eye(ctx, -3, -61, closed);
      eye(ctx, 4, -61, closed);
    }
    const feel = m === "observe" ? Math.sin(s.phase * tau) * 5 : 0;
    line(
      ctx,
      [
        [-3, -68],
        [-9 - feel, -83],
      ],
      ink,
    );
    line(
      ctx,
      [
        [3, -68],
        [10 + feel, -83],
      ],
      ink,
    );
  } else if (id === "spitter") {
    ellipse(ctx, 0, -28, 16, 29, "#b0b8a0");
    for (let i = 0; i < 4; i++) {
      const wave = locomotion
        ? Math.sin(s.phase * tau + (i * Math.PI) / 2) *
          (m === "run" ? 10 : 6) *
          travel
        : 0;
      line(
        ctx,
        [
          [i * 8 - 12, -9],
          [i * 12 - 18 + wave, -Math.max(0, wave)],
        ],
        "#667d65",
        4,
      );
    }
    ctx.save();
    ctx.translate(recoil, -collapse * -16);
    ctx.rotate(
      m === "attack"
        ? pulse * 0.2
        : m === "volley"
          ? -pulse * 0.18
          : m === "turn"
            ? Math.sin(s.phase * tau) * 0.1
            : 0,
    );
    const paintedCap = drawPaintedCore(ctx, id, 0, 0, 92, back);
    if (!paintedCap) {
      const breathe = Math.sin(t * 2) * 1.3;
      ellipse(ctx, 0, -58, 39, 24 + breathe, c[0]);
      shape(
        ctx,
        [
          [-40, -53],
          [-27, -72],
          [0, -83],
          [28, -71],
          [40, -53],
        ],
        c[1],
      );
      for (let i = 0; i < 6; i++)
        ellipse(ctx, i * 12 - 29, -61 - Math.sin(i) * 6, 3.5, 2, "#eee1c1");
    }
    ctx.restore();
    if (!back && !paintedCap) {
      eye(ctx, -6, -32, closed);
      eye(ctx, 7, -32, closed);
    }
    if (m === "attack" || m === "volley")
      ellipse(ctx, 0, -23, 3 + pulse * 4, 2 + pulse * 7, "#5d4a5c");
    if (m === "observe")
      line(
        ctx,
        [
          [14, -34],
          [20, -31],
          [18, -24],
        ],
        c[1],
        3,
      );
  } else {
    leg(false);
    arm(false);
    const coreHeight =
      id === "hero"
        ? 90
        : id === "thorn-warden"
          ? 104
          : id === "brute"
            ? 79
            : id === "pumpkin"
              ? 67
              : id === "coconut" || id === "banana"
                ? 86
                : 80;
    if (!drawPaintedCore(ctx, id, p.hip[0], p.hip[1] + 5, coreHeight, back)) {
      if (id === "hero") {
        shape(
          ctx,
          [
            [p.shoulder[0] - 13, p.shoulder[1] - 2],
            [p.shoulder[0] + 13, p.shoulder[1] + 2],
            [p.hip[0] + 22, p.hip[1] + 8],
            [p.hip[0] - 24, p.hip[1] + 10],
          ],
          c[0],
        );
        shape(
          ctx,
          [
            [p.shoulder[0] - 8, p.shoulder[1] + 3],
            [p.hip[0] - 15, p.hip[1] + 3],
            [p.hip[0] + 4, p.hip[1] + 7],
          ],
          c[1],
        );
        line(
          ctx,
          [
            [p.hip[0] - 15, p.hip[1] - 3],
            [p.hip[0] + 16, p.hip[1] - 3],
          ],
          "#786648",
          5,
        );
        ellipse(ctx, p.head[0], p.head[1], 15, 18, "#e1c49d");
        shape(
          ctx,
          [
            [p.head[0] - 17, p.head[1] - 3],
            [p.head[0] - 13, p.head[1] - 20],
            [p.head[0] + 10, p.head[1] - 22],
            [p.head[0] + 18, p.head[1] - 6],
            [p.head[0] + 4, p.head[1] - 12],
            [p.head[0] - 7, p.head[1] - 2],
          ],
          "#504d3a",
        );
        leaf(ctx, p.head[0] - 5, p.head[1] - 19, 13, 0.65, "#86a272");
        if (back) ellipse(ctx, p.head[0], p.head[1] + 2, 14, 15, "#504d3a");
        else {
          eye(
            ctx,
            p.head[0] + (face === "south" ? -5 : 4),
            p.head[1] + 2,
            closed,
          );
          eye(ctx, p.head[0] + 10, p.head[1] + 2, closed);
        }
        if (back)
          ellipse(ctx, p.shoulder[0], p.shoulder[1] + 19, 13, 18, "#bdab7c");
      } else {
        const x = p.hip[0],
          y = p.hip[1] - 25,
          tall = id === "carrot" || id === "chili",
          bulky = id === "brute" || id === "thorn-warden";
        if (id === "carrot")
          shape(
            ctx,
            [
              [x - 20, y - 31],
              [x + 20, y - 31],
              [x + 14, y + 13],
              [x, y + 29],
              [x - 14, y + 13],
            ],
            c[0],
          );
        else if (id === "chili") {
          ctx.fillStyle = c[0];
          ctx.strokeStyle = ink;
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(x - 12, y - 27);
          ctx.bezierCurveTo(x + 31, y - 48, x + 33, y + 23, x - 14, y + 31);
          ctx.bezierCurveTo(x + 3, y + 10, x - 27, y - 5, x - 12, y - 27);
          ctx.fill();
          ctx.stroke();
        } else if (id === "brute" || id === "thorn-warden")
          shape(
            ctx,
            [
              [x - 30, y - 19],
              [x - 19, y - 35],
              [x + 21, y - 32],
              [x + 34, y - 6],
              [x + 23, y + 28],
              [x - 26, y + 28],
              [x - 36, y + 5],
            ],
            c[0],
          );
        else
          ellipse(
            ctx,
            x,
            y,
            bulky ? 31 : tall ? 17 : id === "wintermelon" ? 30 : 27,
            id === "coconut" ? 26 : tall ? 35 : 30,
            c[0],
          );
        for (let i = -1; i <= 1; i++) {
          ctx.strokeStyle = c[1];
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.ellipse(x + i * 5, y, Math.abs(i) * 9 + 9, 26, 0, -1.3, 1.3);
          ctx.stroke();
        }
        for (let i = 0; i < 3; i++)
          leaf(ctx, x + i * 6 - 6, y - 26, 12 + i * 2, (i - 1) * 0.8, c[1]);
        if (!back) {
          if (bulky) {
            for (const side of [-1, 1]) {
              line(
                ctx,
                [
                  [x + side * 4, y - 6],
                  [x + side * 17, y - 11],
                ],
                "#334d3b",
                5,
              );
              ellipse(
                ctx,
                x + side * 11,
                y - 3,
                3.8,
                closed ? 1 : 2.7,
                "#e4dab1",
                false,
              );
            }
            line(
              ctx,
              [
                [x - 5, y + 9],
                [x + 5, y + 9],
              ],
              ink,
              2,
            );
          } else {
            eye(ctx, x - 7, y - 2, closed);
            eye(ctx, x + 8, y - 2, closed);
            line(
              ctx,
              [
                [x - 3, y + 7],
                [x + 1, y + 9],
                [x + 5, y + 7],
              ],
              ink,
              1.5,
            );
          }
        } else
          line(
            ctx,
            [
              [x, y - 17],
              [x - 3, y + 9],
            ],
            c[1],
            3,
          );
        if (id === "moonherb") {
          for (let i = 0; i < 5; i++)
            ellipse(
              ctx,
              x + Math.cos((i * tau) / 5) * 13,
              y - 28 + Math.sin((i * tau) / 5) * 8,
              7,
              6,
              "#d4c8df",
            );
          ellipse(ctx, x, y - 28, 5, 5, "#eedbac");
        }
        if (id === "coconut") {
          shape(
            ctx,
            [
              [x - 24, y - 16],
              [x - 11, y - 24],
              [x + 12, y - 24],
              [x + 24, y - 15],
              [x + 13, y - 11],
              [x - 12, y - 11],
            ],
            "#dedcc1",
          );
          for (let i = -1; i <= 1; i++)
            leaf(ctx, x, y - 24, 18, i * 1.1, "#81a471");
        }
        if (id === "wintermelon") {
          for (let i = 0; i < 9; i++)
            ellipse(
              ctx,
              x - 21 + (i % 3) * 21,
              y - 21 + Math.floor(i / 3) * 18,
              2.4,
              1.4,
              "#c7dab188",
              false,
            );
          shape(
            ctx,
            [
              [x - 10, y - 25],
              [x, y - 33],
              [x + 10, y - 25],
              [x, y - 19],
            ],
            "#d0e4cf",
          );
        }
        if (id === "banana")
          for (let i = -1; i <= 1; i++)
            leaf(ctx, x + i * 8, y - 14, 27, i * 0.6, "#bac986");
        if (id === "thorn-warden") {
          for (let i = -1; i <= 1; i++)
            line(
              ctx,
              [
                [x + i * 18, y - 26],
                [x + i * 16 + 3, y - 13],
                [x + i * 18 - 4, y + 4],
                [x + i * 17, y + 21],
              ],
              "#3d61454f",
              2,
            );
          for (const side of [-1, 1]) {
            leaf(ctx, x + side * 27, y + 8, 15, side * 0.7, "#8fa572");
            leaf(ctx, x + side * 26, y - 17, 11, side * 0.9, "#bbc58b");
          }
          for (const side of [-1, 1]) {
            line(
              ctx,
              [
                [x + side * 17, y - 25],
                [x + side * 32, y - 47],
                [x + side * 29, y - 68],
              ],
              "#697d53",
              6,
            );
            line(
              ctx,
              [
                [x + side * 30, y - 48],
                [x + side * 43, y - 60],
              ],
              "#697d53",
              4,
            );
            leaf(ctx, x + side * 30, y - 51, 12, side * 0.8, "#b8c790");
          }
          shape(
            ctx,
            [
              [x, y - 5],
              [x + 10, y + 7],
              [x, y + 21],
              [x - 10, y + 7],
            ],
            "#d1ddb0",
          );
        }
        if (id === "sentinel")
          shape(
            ctx,
            [
              [x - 34, y - 15],
              [x - 16, y - 23],
              [x - 7, y],
              [x - 18, y + 26],
              [x - 36, y + 8],
            ],
            "#c3a66a",
          );
      }
    }
    leg(true);
    arm(true);
    if (
      id === "hero" ||
      id === "pumpkin" ||
      id === "carrot" ||
      id === "banana"
    ) {
      const h = p.nearHand;
      ctx.save();
      ctx.translate(...h);
      const tool =
        s.motion === "chop" || s.motion === "mine" || s.motion === "craft";
      ctx.rotate(tool ? -1.3 + Math.sin(s.phase * Math.PI) * 2.6 : 0.25);
      if (s.motion === "water") {
        ellipse(ctx, 7, -1, 12, 9, "#95b5ac");
        line(
          ctx,
          [
            [14, -1],
            [27, -8],
          ],
          "#708f83",
          5,
        );
        for (let i = 0; i < 4; i++)
          ellipse(
            ctx,
            28 + i * 2,
            Math.sin(s.phase * Math.PI) * 10 + i * 5,
            1.2,
            2.5,
            "#b0dcda",
            false,
          );
      } else if (s.motion === "eat") ellipse(ctx, 0, -3, 6, 5, "#c68154");
      else if (
        s.motion !== "plant" &&
        s.motion !== "gather" &&
        s.motion !== "rest"
      ) {
        line(
          ctx,
          [
            [0, 18],
            [0, -34],
          ],
          "#705d43",
          4,
        );
        line(
          ctx,
          [
            [1, 13],
            [1, -28],
          ],
          "#c4b080",
          1,
        );
        if (s.motion === "mine")
          shape(
            ctx,
            [
              [-16, -30],
              [-2, -35],
              [13, -30],
              [20, -21],
              [3, -28],
              [-10, -27],
            ],
            "#a2b9ae",
          );
        else if (tool || id === "pumpkin")
          shape(
            ctx,
            [
              [0, -32],
              [16, -36],
              [18, -23],
              [1, -22],
            ],
            "#a2b9ae",
          );
        else if (id === "banana") leaf(ctx, 0, -20, 19, 0.5, "#c7d594");
        else {
          ellipse(ctx, 0, -37, 7, 9, "#b6d49f");
          line(
            ctx,
            [
              [-6, -35],
              [0, -47],
              [6, -35],
            ],
            "#f0d593",
            2,
          );
        }
      }
      ctx.restore();
    }
  }
  if (["cast", "heal", "volley"].includes(s.motion)) {
    for (let i = 0; i < 6; i++) {
      const a = (i * tau) / 6 + s.phase * 3;
      ellipse(ctx, Math.cos(a) * 38, -50 + Math.sin(a) * 26, 2, 3, c[1], false);
    }
  }
  if (s.audit) {
    for (const [name, j] of Object.entries(p)) {
      ellipse(
        ctx,
        j[0],
        j[1],
        2,
        2,
        name.startsWith("far") ? "#21d9ff" : "#ef76cb",
        false,
      );
    }
  }
  ctx.restore();
}
export function runtimeArtState(
  actor: Animated & {
    velocity?: Vec2;
    facing?: number;
    health?: number;
    maxHealth?: number;
    active?: boolean;
  },
  time: number,
  environment = false,
): ArtState {
  let m: Motion = environment ? "wind" : "idle",
    p = (time % 2) / 2;
  if (actor.motion) {
    m = actor.motion.id;
    const a = actor.motion,
      hit = MOTIONS[m][2] / Math.max(1, MOTIONS[m][0] - 1);
    p =
      a.eventAt && hit > 0
        ? a.age < a.eventAt
          ? (hit * a.age) / a.eventAt
          : hit + ((1 - hit) * (a.age - a.eventAt)) / (a.duration - a.eventAt)
        : Math.min(1, a.age / a.duration);
  } else if (actor.animation) {
    m = actor.animation.clip;
    p = Math.min(1, actor.animation.age / actor.animation.duration);
  } else if (
    actor.velocity &&
    Math.hypot(actor.velocity.x, actor.velocity.y) > 4
  ) {
    m = Math.hypot(actor.velocity.x, actor.velocity.y) > 260 ? "run" : "walk";
    p = ((actor.stride ?? 0) / strideLength(m)) % 1;
  } else if (actor.behavior === "rest" || actor.behavior === "observe") {
    m = actor.behavior;
    p = (time / 3) % 1;
  } else if (!environment && time % 19 > 15) {
    m = "observe";
    p = ((time % 19) - 15) / 4;
  }
  const angle = actor.facing ?? 0;
  return {
    motion: m,
    phase: p,
    time,
    facing:
      Math.sin(angle) < -0.7
        ? "north"
        : Math.sin(angle) > 0.7
          ? "south"
          : Math.cos(angle) < 0
            ? "west"
            : "east",
    health: actor.maxHealth ? actor.health! / actor.maxHealth : 1,
    active: actor.active,
  };
}
export { MOTIONS, motionDuration };
